// Statuswechsel eines Mangeleintrags — und das Datum, das dabei entsteht.
//
// Erledigt wird ein Mangel AUF DER BEGEHUNG, nicht an dem Tag, an dem der
// Bericht fertig geschrieben wird. Wer eine Begehung drei Tage später
// nacharbeitet, meldet den Mangel trotzdem zum Begehungstag frei; sonst steht
// im Bericht ein Erledigungsdatum, an dem niemand auf der Baustelle war.
//
// Pur und ohne DOM (Standing Decision: Fachlogik in testbare Funktionen),
// damit Mängelliste, Historie und Freelancer-Portal dieselbe Regel benutzen
// und nicht drei leicht verschiedene.

import { InspectionItem } from '../types';

/** Heute als YYYY-MM-DD in lokaler Zeit (nicht UTC — sonst kippt der Tag abends). */
export const heutigesDatum = (): string => new Date().toLocaleDateString('sv-SE');

/**
 * Das Erledigungsdatum einer Freimeldung.
 *
 * Ohne bekanntes Begehungsdatum bleibt „heute" der Rückfall: ein ungenaues
 * Datum ist immer noch besser als gar keines, und im Bericht stand an dieser
 * Stelle nie nichts.
 */
export const freimeldeDatum = (
    reportDate?: string | null,
    heute: string = heutigesDatum(),
): string => {
    // Berichte tragen YYYY-MM-DD; ein Archivstand kann eine volle ISO-Zeit
    // mitbringen, die kein <input type="date"> annimmt. Abschneiden statt
    // umrechnen — dieselbe Konvention wie `toYmd` im sendGuard:
    //
    // Das Begehungsdatum wird IMMER aus einem Datumsfeld geschrieben, nie aus
    // einem Zeitpunkt. Ein Zeitanteil stammt daher aus Alt-Importen und traegt
    // eine harmlose Arbeitszeit — abschneiden trifft dort den richtigen Tag.
    // Ein solcher Stempel in lokaler Zeitzone (2026-08-10T22:00:00Z ist in
    // Sommerzeit schon der 11.) liefert den Vortag; das ist bewusst in Kauf
    // genommen, denn die Umrechnung waere geraten (die Zone des Alt-Imports
    // ist unbekannt) und wuerde den Normalfall — den reinen Datumsstring —
    // in westlichen Zeitzonen um einen Tag verschieben.
    //
    // Was nicht wie ein Datum aussieht, wird verworfen: ein „Datum" wie
    // 11.08.2026 im Feld closedDate erscheint im PDF als „Invalid Date".
    const d = (reportDate || '').trim().slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : heute;
};

/**
 * Die Feldänderungen eines Statuswechsels ohne Rückfrage-Dialog.
 *
 * - `closed` bekommt das Begehungsdatum als Erledigungsdatum.
 * - `open` nimmt die Freimeldung zurück, also auch ihr Datum.
 * - alles andere lässt ein vorhandenes Erledigungsdatum stehen: der Wechsel
 *   auf Hinweis/Positiv/Information ist eine Umtypung, keine Rücknahme.
 *
 * Der Desktop führt für den Weg „offener Mangel → erledigt" zusätzlich einen
 * Dialog (Kommentar, Beweisbild, Datum von Hand); dessen Vorbelegung kommt
 * aus `freimeldeDatum`, das Ergebnis schreibt die Liste selbst.
 *
 * NUR die Datumsregel. Ein Statuswechsel in einer Eintragsliste läuft IMMER
 * über `statusWechselAnwenden` — sonst fehlt die Nummernregel weiter unten.
 */
export const statusWechselFelder = (
    neuerStatus: InspectionItem['status'],
    reportDate?: string | null,
    heute: string = heutigesDatum(),
): Partial<InspectionItem> => {
    if (neuerStatus === 'closed') {
        return { status: 'closed', closedDate: freimeldeDatum(reportDate, heute) };
    }
    if (neuerStatus === 'open') {
        return { status: 'open', closedDate: undefined };
    }
    return { status: neuerStatus };
};

// --- Die laufende Nummer ------------------------------------------------------
//
// Eine „Information" (`status === 'general'`) ist eine Vorbemerkung an den
// Leser, kein Ticket. Sie trägt KEINE laufende Nummer — und verbraucht auch
// keine.
//
// ACHTUNG, BEWUSSTE AUSNAHME: Sonst gilt in diesem Modul „Statuswechsel lässt
// Alt-Felder (Ort, Firma, Frist, planCoordinates) verlustfrei stehen, sie
// werden nur nicht angezeigt". Die Nummer ist hier absichtlich die Ausnahme und
// wird beim Wechsel auf `general` wirklich FREIGEGEBEN. Grund: sie ist eine
// knappe, fortlaufende Ressource. Bliebe sie am Eintrag stehen und würde nur
// ausgeblendet, zählte die nächste Vergabe über diese Phantomnummer hinweg und
// risse eine Lücke in die Mängelnummerierung („#7 fehlt, wo ist #7?"). Bitte
// nicht auf „nur ausblenden" zurückbauen.
//
// Gegenstück: bestehende Einträge werden NIE umnummeriert. Die laufende Nummer
// ist die Identität eines Mangels über Berichte hinweg (Fortschreibung, Archiv,
// Freelancer-Portal, Plan-Punkte). Nur der Eintrag, dessen Status sich gerade
// ändert, verliert oder bekommt eine.

/** Was die Nummernregel von einem Eintrag braucht. */
type MitTicketNummer = Pick<InspectionItem, 'ticketNumber'>;

/**
 * Die nächste freie laufende Nummer einer Eintragsliste.
 *
 * Maximum + 1 über den GESAMTEN Bestand (unabhängig von Abschnitt und Status).
 *
 * Damit wird die HÖCHSTE freigegebene Nummer wieder vergeben — und das ist der
 * Normalfall, nicht der Unfall: Am Schreibtisch entsteht jeder Eintrag zuerst
 * als Mangel und bekommt seine Nummer, die Kachel „Information" kommt erst
 * danach. Ohne Recycling verbrennte jede Information eine Nummer und hinterließe
 * genau die Lücke, die wir vermeiden wollen.
 *
 * DER RAND, DEN DAS OFFEN LÄSST: Wird ein Mangel aus einem BEREITS VERSENDETEN
 * Bericht nachträglich zur Information gemacht UND war er der höchste, kann eine
 * neue Feststellung seine Nummer erben — im Altbericht steht dann etwas anderes
 * unter derselben Nummer. In der Praxis kaum erreichbar (ein fortgeschriebener
 * Mangel ist selten der höchste), deshalb bewusst nicht mit einer eigenen
 * Höchststand-Buchführung erschlagen.
 */
export const naechsteFreieTicketNummer = (items: readonly MitTicketNummer[]): number =>
    items.reduce((max, i) => Math.max(max, i.ticketNumber || 0), 0) + 1;

/**
 * Die Nummer, die ein NEU angelegter Eintrag bekommt.
 *
 * Nimmt die Zahl, nicht das Array: manche Aufrufer haben den Bestand hier
 * schon anderweitig ausgewertet. Wer einen Eintrag in eine Liste einfügt,
 * nimmt `neuenEintragEinfuegen` — das vergibt die Nummer erst beim Einfügen.
 */
export const ticketNummerFuerNeuenEintrag = (
    status: InspectionItem['status'],
    naechsteFreie: number,
): number | undefined => (status === 'general' ? undefined : naechsteFreie);

/**
 * Einen neuen Eintrag in den Bestand einfügen — MIT der Nummer, die sich aus
 * genau diesem Bestand ergibt.
 *
 * WARUM DIE NUMMER HIER ENTSTEHT UND NICHT BEIM AUSFÜLLEN DES FORMULARS:
 * Auf der Baustelle wird in Serie erfasst. Der mobile Wizard quittiert dem
 * Prüfer sofort „Gespeichert" und bietet „Nächster Eintrag" an, während die
 * Fotos noch in den Storage laufen — geschrieben wird also Sekunden später.
 * Eine beim Öffnen des Formulars vorberechnete Nummer ist zu diesem Zeitpunkt
 * veraltet, und der zweite Mangel bekäme dieselbe wie der erste. Deshalb
 * rechnet die Nummer erst hier, aus dem Bestand, der wirklich gerade dasteht.
 *
 * Die Regel selbst bleibt unverändert: eine Information bekommt keine Nummer.
 * Eine am Eintrag mitgebrachte Nummer wird bewusst überschrieben — er ist neu,
 * er hat noch keine Identität zu wahren.
 */
export const neuenEintragEinfuegen = (
    bestand: readonly InspectionItem[],
    neuerEintrag: InspectionItem,
    position: 'vorne' | 'hinten' = 'vorne',
): InspectionItem[] => {
    const mitNummer: InspectionItem = {
        ...neuerEintrag,
        ticketNumber: ticketNummerFuerNeuenEintrag(
            neuerEintrag.status,
            naechsteFreieTicketNummer(bestand),
        ),
    };
    return position === 'vorne' ? [mitNummer, ...bestand] : [...bestand, mitNummer];
};

/**
 * Die Nummernänderung eines Statuswechsels — nur für den Eintrag, der wechselt.
 *
 * - auf `general`: die Nummer entfällt (Freigabe, siehe Begründung oben).
 * - von `general` zurück auf einen echten Status: die nächste freie Nummer.
 * - alles andere: keine Änderung. Wer schon eine Nummer hat, behält sie.
 */
export const ticketNummerWechselFelder = (
    neuerStatus: InspectionItem['status'],
    eintrag: MitTicketNummer,
    alleEintraege: readonly MitTicketNummer[],
): Partial<InspectionItem> => {
    if (neuerStatus === 'general') return { ticketNumber: undefined };
    if (eintrag.ticketNumber != null) return {};
    return { ticketNumber: naechsteFreieTicketNummer(alleEintraege) };
};

/**
 * Ein Statuswechsel auf einer Eintragsliste — DER Einstieg für jede Oberfläche.
 *
 * Bündelt Datums- und Nummernregel, damit Mängelliste, mobiler Wizard und
 * Freelancer-Portal nicht drei leicht verschiedene Fassungen bekommen. Die
 * Nummernregel braucht den ganzen Bestand (für die nächste freie Nummer) und
 * lässt sich deshalb nicht aus einer reinen Feld-Funktion heraus anwenden.
 *
 * `weitereFelder` gewinnt zuletzt: der Freimelde-Dialog schreibt sein von Hand
 * gewähltes Datum über die Vorbelegung.
 */
export const statusWechselAnwenden = (
    eintraege: InspectionItem[],
    id: string,
    neuerStatus: InspectionItem['status'],
    reportDate?: string | null,
    weitereFelder?: Partial<InspectionItem>,
    heute: string = heutigesDatum(),
): InspectionItem[] =>
    eintraege.map(i =>
        i.id === id
            ? {
                ...i,
                ...statusWechselFelder(neuerStatus, reportDate, heute),
                ...ticketNummerWechselFelder(neuerStatus, i, eintraege),
                ...weitereFelder,
            }
            : i,
    );

// --- Einträge von aussen hereinholen ------------------------------------------
//
// „Mängel wiederherstellen" holt Einträge aus einem archivierten Bericht zurück
// in den aktuellen; der Berichts-Import über die Einstellungen tut dasselbe mit
// einer Datei. Beide bringen die laufenden Nummern von damals mit — und
// zwischenzeitlich kann dieselbe Nummer im aktuellen Bericht längst an einen
// ANDEREN Sachverhalt vergeben sein.
//
// WER AUSWEICHT: der Hereinkommende. Die Einträge, die schon im Bericht stehen,
// werden nie umnummeriert — ihre Nummer steht bereits in versendeten PDFs, im
// Plananhang und im Freelancer-Portal. Der Wiederhergestellte war dagegen aus
// dem aktuellen Bericht verschwunden; seine Nummer ist dort nichts wert.

/** Ein Nummernwechsel beim Hereinholen — Stoff für die Meldung an den Nutzer. */
export interface TicketNummerWechsel {
    id: string;
    titel: string;
    alteNummer: number;
    neueNummer: number;
}

export interface EinordnungsErgebnis {
    /** Die hereinkommenden Einträge, kollisionsfrei nummeriert. */
    eintraege: InspectionItem[];
    /** Was dabei umnummeriert wurde — leer heisst: nichts kollidierte. */
    umnummeriert: TicketNummerWechsel[];
}

/**
 * Hereinkommende Einträge kollisionsfrei in einen Bestand einordnen.
 *
 * - Information (`status === 'general'`) → KEINE Nummer, egal welche sie
 *   mitbringt. Sie ist eine Vorbemerkung an den Leser, kein Ticket; die Regel
 *   gilt hier genauso wie bei der Neuanlage.
 * - Nummer noch frei → sie bleibt. Die Wiederherstellung soll den Bericht von
 *   damals zurückbringen, nicht umschreiben.
 * - Nummer schon vergeben → der Hereinkommende bekommt die nächste freie.
 *
 * Der Bestand wird nicht angefasst und nicht mit zurückgegeben — der Aufrufer
 * hängt die Einträge an, wie es seine Ansicht braucht. Er wird auch NICHT
 * bereinigt: ein Alteintrag, der als Information mit gespeicherter Nummer
 * dasteht, behält sie (er zeigt sie nur nicht) — hereingeholt wird hier
 * ausschliesslich Neues.
 */
export const eintraegeKollisionsfreiEinordnen = (
    hereinkommend: readonly InspectionItem[],
    bestand: readonly MitTicketNummer[],
): EinordnungsErgebnis => {
    const belegt = new Set<number>();
    for (const i of bestand) {
        if (i.ticketNumber != null && i.ticketNumber > 0) belegt.add(i.ticketNumber);
    }
    // Zuerst den Informationen ihre mitgebrachte Nummer nehmen — VOR dem
    // Zählerstand. Eine solche Phantomnummer ist im Zielbericht nichts wert,
    // schöbe aber das Maximum hoch und risse damit genau die Lücke in die
    // Mängelnummerierung, die die Nummernregel oben vermeiden soll.
    const bereinigt = hereinkommend.map(e =>
        e.status === 'general' && e.ticketNumber != null ? { ...e, ticketNumber: undefined } : e,
    );
    // Der Zähler startet über ALLEM, was hier zusammenkommt — auch über den
    // Nummern der Hereinkommenden, die ihre behalten dürfen. Sonst vergäbe die
    // Auflösung eine Nummer, die ein paar Einträge weiter noch gebraucht wird.
    let naechste = naechsteFreieTicketNummer([...bestand, ...bereinigt]);
    const umnummeriert: TicketNummerWechsel[] = [];

    const eintraege = bereinigt.map(eintrag => {
        const alt = eintrag.ticketNumber;
        if (alt == null || alt <= 0) return eintrag;
        if (!belegt.has(alt)) {
            belegt.add(alt);
            return eintrag;
        }
        while (belegt.has(naechste)) naechste++;
        const neu = naechste++;
        belegt.add(neu);
        umnummeriert.push({ id: eintrag.id, titel: eintrag.title, alteNummer: alt, neueNummer: neu });
        return { ...eintrag, ticketNumber: neu };
    });

    return { eintraege, umnummeriert };
};

/**
 * Der Text, der die Umnummerierung sichtbar macht.
 *
 * Ein stiller Nummernwechsel ist genau die Überraschung, die wir vermeiden
 * wollen: der Prüfer sucht später „#7" und findet einen anderen Mangel. Leere
 * Liste → leerer Text, dann gibt es nichts zu melden.
 */
export const beschreibeNummernWechsel = (wechsel: readonly TicketNummerWechsel[]): string => {
    if (!wechsel.length) return '';
    const zeilen = wechsel.map(w => `#${w.alteNummer} → #${w.neueNummer}: ${w.titel || 'Ohne Titel'}`);
    return `${wechsel.length === 1 ? 'Eine Nummer war' : `${wechsel.length} Nummern waren`}`
        + ' im aktuellen Bericht bereits vergeben und wurde'
        + `${wechsel.length === 1 ? '' : 'n'} neu vergeben:\n`
        + zeilen.join('\n');
};
